import { toast } from 'react-hot-toast'
import { Dispatch, SetStateAction } from 'react'
import { PostgrestError, PostgrestFilterBuilder } from '@supabase/postgrest-js'
import { supabaseClient } from './supabase'

export type APIResponse = {
  ok: boolean
  data?: any
  error?: PostgrestError | string
}

export const returnResponseData = (data: any): APIResponse => ({
  ok: true,
  data: data,
})
export const returnResponseError = (
  error: PostgrestError | any | string
): APIResponse => ({
  ok: false,
  error: error,
})

// Promise<{ error: PostgrestError | null; data: any }>
export const processRequest = async (
  request:
    | PostgrestFilterBuilder<any, any, any>
    | Promise<{ data: any; error: any }>
) => {
  const response = await request

  if (response?.error !== null) {
    return returnResponseError(response.error)
  }

  return returnResponseData(response?.data ?? null)
}

export const processAPIResponse = ({
  response,
  errorMessage,
  successMessage,
  successCallback,
}: {
  response: APIResponse
  errorMessage: string
  successMessage?: string
  successCallback?: (data: any) => void
}) => {
  if (response.ok) {
    if (successMessage) {
      toast.success(successMessage)
    }

    if (successCallback) {
      successCallback(response.data)
    }
  } else {
    toast.error(errorMessage)
  }
}

type handleAPIRequestType = {
  errorMessage: string
  loadingMessage?: string
  successMessage?: string | null
  successCallback?: (data: any) => void
  busyState?: Dispatch<SetStateAction<boolean>>
  errorCallback?: (error: APIResponse['error']) => void
}

export const handleAPIRequest = async (
  request: Promise<APIResponse>,
  {
    busyState,
    errorCallback,
    errorMessage,
    loadingMessage,
    successCallback,
    successMessage = 'Done!',
  }: handleAPIRequestType
): Promise<void> => {
  busyState?.(true)
  let loader = loadingMessage ? toast.loading(loadingMessage) : undefined

  const response = await request

  toast.dismiss(loader)
  busyState?.(false)

  if (!response.ok) {
    toast.error(errorMessage)
    errorCallback?.(response.error)
    return
  }

  if (successMessage) {
    toast.success(successMessage)
  }

  successCallback?.(response.data)
}

export const localAPIRequest = async ({
  data,
  action,
  method = 'post',
  withAuth = true,
  endpoint = '/api/request',
}: {
  data?: any
  action: string
  endpoint?: string
  withAuth?: boolean
  method?: 'post' | 'get'
}) => {
  const session = await supabaseClient.auth.getSession()
  if (!session.data) {
    return returnResponseError('no_session')
  }

  const body = JSON.stringify({
    action: action,
    data: data,
  })

  const response = await fetch(endpoint, {
    mode: 'cors',
    method: method,
    cache: 'no-cache',
    credentials: 'same-origin',
    body: method == 'post' ? body : undefined,
    headers: {
      'content-Type': 'application/json',
      authorization: withAuth
        ? 'Bearer ' + session.data?.session?.access_token
        : '',
    },
  })

  return response.json()
}
